import React, { useState } from "react";
import { Ticket } from "../../../../../models/Ticket";
import { Exercise } from "../../../../../models/Exercise";
import "./SingleReservation.scss";
import { EAInfoTile } from "../../../../shared/InfoTile";
import useDesktopSize from "../../../../../../utils/hooks/useDesktopSize";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import EAIconButton from "../../../../shared/Buttons/EAIconButton";
import { MdChevronRight } from "react-icons/md";
import EAButton from "../../../../shared/Buttons/EAButton";
import ReservationTag from "./ReservationTag";
import ReservationDetailsModal from "./ReservationDetailsModal";
import EAChip from "../../../../shared/Chip/EAChip";
import EATooltip from "../../../../shared/EATooltip/EATooltip";
import useFacility from "../../../../../../utils/hooks/useFacility";

interface IProps {
  ticket: Ticket;
  exercise: Exercise;
  onRefreshTickets?: () => void;
  variant?: "default" | "minimal";
}

const SingleReservation: React.FC<IProps> = ({ ticket, exercise, onRefreshTickets, variant = "default" }) => {
  const isMinimal = variant === "minimal";
  const isDesktop = useDesktopSize();
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const facilityIds = useFacility();
  const showAllStatuses = facilityIds === "1243" || facilityIds === "1241";

  const isEventPassed = new Date(exercise.startTime) <= new Date();

  const getTileColor = () => {
    if (isEventPassed) return "gray";
    return "primary";
  };

  const getDateLabel = () => {
    return format(new Date(exercise.startTime), "dd.MM.yyyy");
  };

  const getHourLabel = () => {
    if (exercise.startTime && exercise.startTime !== 1789524000000) {
      return `• ${format(new Date(exercise.startTime), "HH:mm")}`;
    }
    return "";
  };

  const getInstructorLabel = () => {
    if (exercise.deputyHumanName) {
      return `• ${exercise.deputyHumanName}`;
    }
    if (exercise.humanName) {
      return `• ${exercise.humanName}`;
    }
    return "";
  };

  // Determine past event status chip
  const getPastEventStatusChip = () => {
    if (showAllStatuses && exercise.status === "A") {
      return (
        <EATooltip content={t("nieobecny_tooltip")}>
          <EAChip label={t("nieobecny")} variant="warning" />
        </EATooltip>
      );
    }
    if (showAllStatuses && exercise.status === "U") {
      return (
        <EATooltip content={t("wykorzystany_tooltip")}>
          <EAChip label={t("wykorzystany")} variant="success" />
        </EATooltip>
      );
    }
    if (!showAllStatuses && exercise.status === "A") {
      return (
        <EATooltip content={t("obecny_tooltip")}>
          <EAChip label={t("obecny")} variant="success" />
        </EATooltip>
      );
    }
    return null;
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <div
        className={`single-reservation__container ${
          !isDesktop ? "single-reservation__container--mobile-clickable" : ""
        } ${isMinimal ? "single-reservation__container--minimal" : ""}`}
        onClick={!isDesktop ? handleOpenModal : undefined}
      >
        {!isMinimal && (
          <div className="single-reservation__left-col">
            {isDesktop && <EAInfoTile date={new Date(exercise.startTime)} color={getTileColor()} />}
            <div className="single-reservation__left-col-content">
              {isEventPassed ? (
                // Past reservation - show chips instead of tags
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <EATooltip content={t("minelo_tooltip")}>
                    <EAChip label={t("minelo")} variant="dark" />
                  </EATooltip>
                  {getPastEventStatusChip()}
                </div>
              ) : (
                // Upcoming reservation - show tags
                <ReservationTag exercise={exercise} />
              )}
              <h5>{exercise.name}</h5>
              <span className={`body-text-small ${isEventPassed ? "text-color-light-gray" : "text-color-dark-gray"}`}>
                {getDateLabel()} {getHourLabel()} {getInstructorLabel()}
              </span>
            </div>
          </div>
        )}
        {isMinimal && (
          <div
            className={`single-reservation__compact-content ${
              isMinimal ? "single-reservation__compact-content--minimal" : ""
            }`}
          >
            {isEventPassed ? (
              // Past reservation - show chips instead of tags
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <EATooltip content={t("minelo_tooltip")}>
                  <EAChip label={t("minelo")} variant="dark" />
                </EATooltip>
                {getPastEventStatusChip()}
              </div>
            ) : (
              // Upcoming reservation - show tags
              <ReservationTag exercise={exercise} />
            )}
            <h5>{exercise.name}</h5>
            <span className={`body-text-small ${isEventPassed ? "text-color-light-gray" : "text-color-dark-gray"}`}>
              {getDateLabel()} {getHourLabel()} {getInstructorLabel()}
            </span>
          </div>
        )}

        <div className="single-reservation__right-col">
          {isDesktop ? (
            <EAButton title={t("szczegoly")} variant="outlined" onClick={handleOpenModal} />
          ) : (
            <EAIconButton
              title={t("szczegoly")}
              icon={<MdChevronRight />}
              variant="icon"
              color="primary"
              onClick={handleOpenModal}
            />
          )}
        </div>
      </div>

      <ReservationDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        exercise={exercise}
        ticket={ticket}
        onRefreshReservations={onRefreshTickets}
      />
    </>
  );
};

export default SingleReservation;
